import { Formik, FormikProps } from 'formik';
import React, { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { theme as currentTheme } from '../../../../common/utils/helpers';
import { attachmentsActions } from '../../../../state/ducks/attachments';
import { GroupTag } from '../../../../state/ducks/auth/types';
import { companyActions, companySelectors } from '../../../../state/ducks/company';
import { Company, WhiteLabelingEnv } from '../../../../state/ducks/company/types';
import { ThemeEnv } from '../../../layout/theme/utils/types';
import useActionCreator from '../../../hooks/useActionCreator';
import useAsync from '../../../hooks/useAsync';
import useGetHasTag from '../../../hooks/useGetHasTag';
import { toWhiteLabelingBodyValues, toWhiteLabelingInitialValues } from '../../utils/transform';
import WhiteLabelingManagementPresenter from './WhiteLabelingManagement.presenter';
import { UploadType, WhiteLabelingUploadType } from './types';

const THEME_KEYS = [ThemeEnv.DEV_THEME, ThemeEnv.DEMO_THEME, ThemeEnv.SANDBOX_THEME, ThemeEnv.PRODUCTION_THEME];
// Look up the theme actually rendered in the app (matches Logo.presenter) before falling back to any other theme
const THEME_LOOKUP_ORDER = [currentTheme as ThemeEnv, ...THEME_KEYS.filter((key) => key !== currentTheme)];

const WhiteLabelingManagementContainer: React.FunctionComponent<{}> = () => {
  const dispatch = useDispatch();

  const [uploadType, setUploadType] = useState<UploadType | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const formikPropsRef = useRef<FormikProps<any> | null>(null);

  const changeCompanyAction = useActionCreator(companyActions.updateCompanyMine);
  const whiteLabeling = useSelector(companySelectors.getAllWhiteLabeling);
  const initialValues = toWhiteLabelingInitialValues(whiteLabeling);
  const canManageWhiteLabeling = useGetHasTag(GroupTag.COMPANY_CONFIG_WHITELABELING);

  const async = useAsync({
    onSuccess: (data?: Company) => {
      if (!data) {
        return;
      }
      dispatch(companyActions.selectCompany(data));
    },
  });

  const asyncS3link = useAsync({
    onSuccess: (data?: { url: string }) => {
      if (!data?.url || !file || !formikPropsRef.current || !uploadType) {
        return;
      }

      const cleanUrl = data.url.substring(0, data.url.indexOf('?'));

      // Parse current whiteLabeling JSON, update the field in ALL themes, and stringify back
      const currentValues = formikPropsRef.current.values;
      const whiteLabel = JSON.parse(currentValues.whiteLabeling || '{}');

      // Update the uploadType (logo or avatar) in all theme objects
      THEME_KEYS.forEach((themeKey) => {
        if (whiteLabel[themeKey]) {
          whiteLabel[themeKey][uploadType] = cleanUrl;
        }
      });

      formikPropsRef.current.setFieldValue('whiteLabeling', JSON.stringify(whiteLabel));

      // Upload image to S3 with callback to submit form after upload completes
      const onImageUploadSuccess = () => {
        // Submit form to update company with new S3 link
        if (formikPropsRef.current?.submitForm) {
          formikPropsRef.current.submitForm().catch(() => {
            // Handle submission error silently
          });
        }
      };

      dispatch(attachmentsActions.uploadImage(data.url, file, onImageUploadSuccess));
    },
  });

  if (!canManageWhiteLabeling) {
    return null;
  }

  const createFileChangeHandler = (type: UploadType) => {
    return (event: React.FormEvent<HTMLInputElement>) => {
      const selectedFile = event.currentTarget.files && event.currentTarget.files[0];
      setFile(selectedFile);
      setUploadType(type);

      if (!selectedFile) {
        return;
      }

      dispatch(attachmentsActions.requestAvatarS3link(selectedFile.name, asyncS3link));
    };
  };

  const handlePoLogoSameAsLogo = (checked: boolean) => {
    if (!formikPropsRef.current) {
      return;
    }

    const currentValues = formikPropsRef.current.values;
    const whiteLabel = JSON.parse(currentValues.whiteLabeling || '{}');

    if (checked) {
      // Copy logo URL to all theme's poLogo
      THEME_KEYS.forEach((themeKey) => {
        if (whiteLabel[themeKey]) {
          whiteLabel[themeKey].poLogo = whiteLabel[themeKey].logo;
        }
      });
    } else {
      // Clear poLogo when unchecked
      THEME_KEYS.forEach((themeKey) => {
        if (whiteLabel[themeKey]) {
          whiteLabel[themeKey].poLogo = '';
        }
      });
    }

    formikPropsRef.current.setFieldValue('whiteLabeling', JSON.stringify(whiteLabel));

    // Submit form to trigger patch request
    formikPropsRef.current.submitForm().catch(() => {
      // Handle submission error silently
    });
  };

  const onSubmit = (values: any) => {
    async.start(changeCompanyAction, toWhiteLabelingBodyValues(values), async);
  };

  const render = (props: FormikProps<any>) => {
    // Store formikProps for use in asyncS3link callback
    formikPropsRef.current = props;

    // Parse whiteLabeling JSON and extract logo/avatar/poLogo from the first theme that defines each field
    let whiteLabel = { logo: '', avatar: '', poLogo: '' };
    try {
      const parsedData = props.values.whiteLabeling ? JSON.parse(props.values.whiteLabeling) : {};
      // Fields aren't always synced across every theme, so resolve each one independently,
      // preferring the theme actually rendered in the app
      const firstThemeValue = (field: keyof WhiteLabelingEnv) => (
        THEME_LOOKUP_ORDER.map((key) => parsedData[key]?.[field]).find(Boolean) || ''
      );
      whiteLabel = {
        logo: firstThemeValue('logo'),
        avatar: firstThemeValue('avatar'),
        poLogo: firstThemeValue('poLogo'),
      };
    } catch (e) {
      // If parsing fails, use defaults
    }

    return (
      <WhiteLabelingManagementPresenter
        asyncState={async.asyncState}
        asyncS3linkState={asyncS3link.asyncState}
        onLogoChange={createFileChangeHandler(WhiteLabelingUploadType.LOGO)}
        onAvatarChange={createFileChangeHandler(WhiteLabelingUploadType.AVATAR)}
        onPoLogoChange={createFileChangeHandler(WhiteLabelingUploadType.PO_LOGO)}
        onPoLogoSameAsLogo={handlePoLogoSameAsLogo}
        whiteLabel={whiteLabel}
        {...props}
      />
    );
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      enableReinitialize={true}
    >
      {render}
    </Formik>
  );
};

export default WhiteLabelingManagementContainer;
